import { useState, useMemo } from "react";
import { Lead } from "../types/lead";
import { LeadCard } from "./LeadCard";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Search, Filter, Sparkles } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { ApplyLeadConfirmation } from "./ApplyLeadConfirmation";
import { Alert, AlertDescription } from "./ui/alert";

interface LeadsProProps {
  leads: Lead[];
  onViewDetails: (leadId: string) => void;
  onApplyLead?: (leadId: string) => void;
  onDeclineLead?: (leadId: string) => void;
  canRespondToJobs?: boolean;
  canAcceptJobs?: boolean;
  setShowPlanModal:any
}

export function LeadsPro({
  leads,
  onViewDetails,
  onApplyLead,
  onDeclineLead,
  canRespondToJobs = false,
  canAcceptJobs = false,
  setShowPlanModal
}: LeadsProProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [pendingLeadId, setPendingLeadId] = useState<string | null>(null);
  const [showApplyConfirmation, setShowApplyConfirmation] = useState(false);

  // ✅ Get unique categories from leads list
  const uniqueCategories = useMemo(() => {
    const allCats = leads.map((lead) => lead.category?.trim().toLowerCase());
    const unique = Array.from(new Set(allCats.filter(Boolean))); // removes duplicates & empty
    return ["all", ...unique]; // Add "all" option at top
  }, [leads]);

  // ✅ Filtering
  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      lead.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.location.city.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategory === "all" || lead.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // ✅ Sorting
  const sortedLeads = filteredLeads.sort((a, b) => {
    if (sortBy === "date") {
      return (
        new Date(b.postedDate).getTime() - new Date(a.postedDate).getTime()
      );
    } else if (sortBy === "budget") {
      // Adjust if budget is a number instead of {min,max}
      return Number(b.budget) - Number(a.budget);
    }
    return 0;
  });

  // ✅ Apply Lead Flow
  const handleApplyLead = (leadId: string) => {
    if(onApplyLead){
      onApplyLead(leadId);

    }
  };

 


  // ✅ Render
  return (
    <div className="lead-management-content">
      <div className="">
        {/* <Alert
  style={{
    background: "linear-gradient(to right, #eff6ff, #eef2ff)",
    border: "1px solid #bfdbfe",
    cursor: "pointer",
    transition: "box-shadow 0.2s ease",
  }}
  className="flex"
  onMouseEnter={(e) => {
    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.boxShadow = "none";
  }}
  onClick={() => setShowPlanModal(true)}
>
  <Sparkles
    style={{
      height: "20px",
      width: "20px",
      color: "#2563eb",
    }}
  />

  <AlertDescription
    style={{
      marginLeft: "8px",
      color: "#1e3a8a",
      fontSize: "14px",
      lineHeight: "20px",
    }}
  >
    <span style={{ fontWeight: 500 }}>Are you not getting leads?</span> Try to come
    in featured contracts and get faster leads!
    <span
      style={{
        marginLeft: "8px",
        color: "#2563eb",
        textDecoration: "underline",
      }}
    >
      Click to learn more →
    </span>
  </AlertDescription>
</Alert> */}



        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="size-5" />
            <div>Filter Leads</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="app-search-field">
              <Search className="app-field-icon" />
              <Input
                placeholder="Search leads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* ✅ Dynamic Category Filter */}
            <Select
              value={selectedCategory}
              onValueChange={setSelectedCategory}
            >
              <SelectTrigger>
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {uniqueCategories.map((cat) => (
                  <SelectItem key={cat} value={cat} className="p-1 capitalize">
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort Filter */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Date Created</SelectItem>
                <SelectItem value="budget">Budget</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Active Filters Display */}
          {(searchQuery || selectedCategory !== "all" || sortBy !== "date") && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
              <span className="text-muted-foreground">Active filters:</span>
              {searchQuery && <Badge>Search: {searchQuery}</Badge>}
              {selectedCategory !== "all" && (
                <Badge>Category: {selectedCategory}</Badge>
              )}
              {sortBy !== "date" && <Badge>Sort By: {sortBy}</Badge>}
            </div>
          )}
        </div>

        {/* Results Summary */}
        <div className="mb-4">
          <p className="text-muted-foreground">
            Showing {sortedLeads.length} of {leads.length} available leads
          </p>
        </div>

        {(!canRespondToJobs || !canAcceptJobs) && (
          <Alert className="mb-4 border-amber-200 bg-amber-50">
            <AlertDescription className="text-amber-900">
              Accepting requires `RESPOND_JOB` and `MANAGE_BILLING`; declining requires `RESPOND_JOB`.
            </AlertDescription>
          </Alert>
        )}

        {/* Leads Grid */}
        {sortedLeads.length > 0 ? (
          <div className="grid grid-cols-1 gap-6">
            {sortedLeads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                onViewDetails={onViewDetails}
                onApplyLead={canAcceptJobs ? handleApplyLead : undefined}
                onDeclineLead={canRespondToJobs ? onDeclineLead : undefined}
                showActionButtons={true}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-muted-foreground">
              No leads found matching your criteria. Try adjusting your filters.
            </p>
          </div>
        )}

    
      </div>
    </div>
  );
}
